import React, { useRef, useMemo, useCallback, useState } from 'react';
import { observer } from 'mobx-react-lite';
import { useTranslation } from 'react-i18next';
import { X, Plus, Loader2 } from 'lucide-react';
import { CompanyAddFormViewModel } from '../../view-model/professional/company/company-add-form-view-model';
import { CompanyEditFormViewModel } from '../../view-model/professional/company/company-edit-form-view-model';
import { compressImages } from '../../utils/image-compression';

interface CompanyGalleryPhotosProps {
  viewModel: CompanyAddFormViewModel | CompanyEditFormViewModel;
}

const MAX_PHOTOS = 5;

/**
 * Composant réutilisable pour gérer l'upload et l'affichage des photos de galerie de l'entreprise
 * Utilisé dans les formulaires d'ajout et d'édition
 *
 * @param viewModel - Instance du ViewModel (Add ou Edit)
 */
const CompanyGalleryPhotos = observer(({ viewModel }: CompanyGalleryPhotosProps) => {
  const { t } = useTranslation();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [isCompressing, setIsCompressing] = useState(false);

  const handleClickUpload = useCallback(() => {
    fileInputRef.current?.click();
  }, []);

  const handleFileChange = useCallback(async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files || files.length === 0) return;

    try {
      setIsCompressing(true);

      // Convertir FileList en Array
      const fileArray = Array.from(files);

      // Compresser toutes les images en parallèle
      const compressedFiles = await compressImages(fileArray);

      // Créer un nouvel événement avec les fichiers compressés
      const dataTransfer = new DataTransfer();
      compressedFiles.forEach(file => dataTransfer.items.add(file));

      // Créer un nouvel événement avec les fichiers compressés
      const newEvent = {
        ...e,
        target: {
          ...e.target,
          files: dataTransfer.files,
        },
      } as React.ChangeEvent<HTMLInputElement>;

      viewModel.handleGalleryPhotosChange(newEvent);
    } catch (error) {
      console.error('Erreur lors de la compression:', error);
      // En cas d'erreur, utiliser les fichiers originaux
      viewModel.handleGalleryPhotosChange(e);
    } finally {
      setIsCompressing(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  }, [viewModel]);

  const canAddMorePhotos = useMemo(
    () => viewModel.galleryPhotoPreviewUrls.length < MAX_PHOTOS,
    [viewModel.galleryPhotoPreviewUrls.length]
  );

  return (
    <div className="space-y-4">
      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
        {t('company.form.galleryPhotos', 'Galerie de photos')}
      </label>

      {/* Prévisualisations des fichiers sélectionnés */}
      {viewModel.galleryPhotoPreviewUrls.length > 0 && (
        <div>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
            {viewModel.galleryPhotoPreviewUrls.map((url: string, index: number) => (
              <div
                key={`preview-${index}`}
                className="relative aspect-square rounded-lg overflow-hidden border-2 border-gray-300 dark:border-gray-600"
              >
                <img
                  src={url}
                  alt={`Prévisualisation ${index + 1}`}
                  className="w-full h-full object-cover"
                />
                {index === 0 && (
                  <div className="absolute top-2 left-2 bg-primary/80 text-white text-xs px-2 py-1 rounded">
                    {t('company.form.mainPhoto', 'Photo principale')}
                  </div>
                )}
                <button
                  type="button"
                  onClick={() => viewModel.removeGalleryPhoto(index)}
                  className="absolute top-2 right-2 bg-red-500/80 text-white p-1 rounded-full hover:bg-red-600/80 transition-colors"
                >
                  <X size={16} />
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Bouton d'ajout de photos */}
      {canAddMorePhotos && (
        <div>
          <button
            type="button"
            onClick={handleClickUpload}
            disabled={isCompressing}
            className="w-full border-2 border-dashed border-gray-300 dark:border-gray-700 rounded-lg p-6 flex flex-col items-center justify-center hover:border-primary dark:hover:border-primary-light transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isCompressing ? (
              <>
                <Loader2 size={24} className="text-primary animate-spin mb-2" />
                <span className="text-sm text-gray-500 dark:text-gray-400">
                  {t('company.form.compressingImages', 'Compression des images...')}
                </span>
              </>
            ) : (
              <>
                <Plus size={24} className="text-gray-400 dark:text-gray-500 mb-2" />
                <span className="text-sm text-gray-500 dark:text-gray-400">
                  {t('company.form.addPhotos', 'Ajouter des photos')}
                </span>
                <span className="text-xs text-gray-400 dark:text-gray-500 mt-1">
                  {t('company.form.photoFormat', 'Format: JPG, PNG, WEBP (optimisées automatiquement)')}
                </span>
              </>
            )}
          </button>
        </div>
      )}

      <input
        ref={fileInputRef}
        type="file"
        accept="image/png, image/jpeg, image/jpg, image/webp"
        onChange={handleFileChange}
        multiple
        className="hidden"
      />

      <div className="text-xs text-gray-500 dark:text-gray-400">
        <p>{t('company.form.galleryPhotoInfo', 'La première photo sera utilisée comme photo principale')}</p>
        <p>{t('company.form.maxPhotos', `Maximum ${MAX_PHOTOS} photos`)}</p>
      </div>

      {/* Affichage de l'erreur si présente */}
      {viewModel.errors.galleryPhoto && (
        <p className="text-xs text-red-500">{viewModel.errors.galleryPhoto}</p>
      )}
    </div>
  );
});

export default CompanyGalleryPhotos;
